import api from "./api";

/**
 * The diary: availability, holds, appointments and blocked time.
 *
 * Times sent to the API are **branch-local wall clock** (`"2026-09-01 14:30"`),
 * because that is what a receptionist types and what a customer means. The
 * server converts them once, against the branch's own IANA timezone. Times that
 * come back arrive twice — a UTC instant to compute with and a branch-local
 * string to display — and the diary uses whichever it actually needs.
 */

/**
 * A fresh Idempotency-Key.
 *
 * Booking writes require one, so a request retried after a dropped connection
 * settles once instead of booking twice. The key belongs to the *operation*, so
 * it is generated where the operation begins and reused across retries of that
 * same attempt — never regenerated per HTTP call, which would defeat the point.
 */
export const newIdempotencyKey = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `k-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;

const withKey = (key) => ({ headers: { "Idempotency-Key": key } });

/* ------------------------------------------------------------ availability */

/**
 * GET /business/availability — bookable starts for one service on one
 * branch-local date. Omit `staffId` to ask across every eligible provider.
 */
export const fetchAvailability = async (
  branchId,
  serviceId,
  date,
  { staffId = null, granularity = null } = {}
) => {
  const params = { branch_id: branchId, service_id: serviceId, date };

  if (staffId) params.staff_id = staffId;
  if (granularity) params.granularity = granularity;

  const { data } = await api.get("/business/availability", { params });
  return { slots: data.data.slots, meta: data.meta };
};

/**
 * GET /business/branches/{branch}/diary — every layer the grid draws:
 * appointments, blocked time, provider schedules with their breaks and leave,
 * and the branch's own opening hours.
 */
export const fetchDiary = async (branchId, from, to = null, providerIds = []) => {
  const params = { from };
  if (to) params.to = to;
  // provider_ids[]=…; empty = every provider. The API answers 422 for anybody
  // outside meta.provider_options.
  if (providerIds.length) params.provider_ids = providerIds;

  const { data } = await api.get(`/business/branches/${branchId}/diary`, { params });
  return { ...data.data, meta: data.meta };
};

/* -------------------------------------------------------------- appointments */

/** GET /business/appointments */
export const fetchAppointments = async ({ page = 1, perPage = 15, filters = {} } = {}) => {
  const params = { page, per_page: perPage };

  if (filters.branch) params.branch = filters.branch;
  if (filters.status) params.status = filters.status;
  if (filters.staff) params.staff = filters.staff;
  if (filters.client) params.client = filters.client;
  if (filters.search) params.search = filters.search;
  if (filters.from) params.from = filters.from;
  if (filters.to) params.to = filters.to;

  const { data } = await api.get("/business/appointments", { params });
  return { appointments: data.data.appointments, meta: data.meta };
};

/** GET /business/appointments/{id} */
export const fetchAppointment = async (id) => {
  const { data } = await api.get(`/business/appointments/${id}`);
  return data.data.appointment;
};

/**
 * POST /business/appointments — take a booking.
 *
 * `values.client` creates a walk-in inline; `values.clientId` names somebody
 * already known. One or the other.
 *
 * A 409 here is not a failure of the request — it means the slot went to
 * somebody else between offering it and taking it, which callers should surface
 * as "pick another time" rather than as an error.
 */
export const createAppointment = async (values, idempotencyKey) => {
  const payload = {
    branch_id: values.branchId,
    services: values.services.map((line) => ({
      service_id: line.serviceId,
      staff_id: line.staffId || null,
      starts_at: line.startsAt,
      ...(line.price != null && line.price !== "" ? { price: Number(line.price) } : {}),
    })),
    status: values.status || "confirmed",
    source: values.source || "portal",
  };

  if (values.clientId) {
    payload.client_id = values.clientId;
  } else if (values.client) {
    payload.client = {
      name: values.client.name,
      phone: values.client.phone || null,
      email: values.client.email || null,
    };
  }

  if (values.notes) payload.notes = values.notes;
  if (values.holdToken) payload.hold_token = values.holdToken;

  const { data } = await api.post("/business/appointments", payload, withKey(idempotencyKey));
  return data.data.appointment;
};

/**
 * PUT /business/appointments/{id}/status — confirm, check in, start, complete,
 * cancel, no-show.
 *
 * Which moves are legal comes from the appointment's own `can_transition_to`,
 * so the buttons a screen renders are exactly what the API will accept.
 */
export const transitionAppointment = async (id, status, reason, idempotencyKey) => {
  const { data } = await api.put(
    `/business/appointments/${id}/status`,
    { status, ...(reason ? { reason } : {}) },
    withKey(idempotencyKey ?? newIdempotencyKey())
  );
  return data.data.appointment;
};

/**
 * PUT /business/appointments/{id} — move a booking.
 *
 * `startsAt` is branch-local wall clock. Every line keeps its offset from the
 * start of the visit, so a two-service booking stays in order. Prices are not
 * re-snapshotted: moving a booking is not renegotiating it.
 *
 * Carries an Idempotency-Key like every other booking write.
 */
/**
 * PUT /business/appointments/{id}/services/{lineId}/provider — put one
 * service on a provider (assign an "any provider" line, or change who does
 * just that service). Its time and the other lines stay as they are; the
 * engine checks eligibility (422) and that the provider is free (409).
 */
export const assignAppointmentProvider = async (id, lineId, staffId, idempotencyKey) => {
  const { data } = await api.put(
    `/business/appointments/${id}/services/${lineId}/provider`,
    { staff_id: staffId },
    withKey(idempotencyKey ?? newIdempotencyKey())
  );
  return data.data.appointment;
};

export const rescheduleAppointment = async (id, startsAt, staffId = null, idempotencyKey) => {
  const { data } = await api.put(
    `/business/appointments/${id}`,
    { starts_at: startsAt, ...(staffId ? { staff_id: staffId } : {}) },
    withKey(idempotencyKey ?? newIdempotencyKey())
  );
  return data.data.appointment;
};

/* --------------------------------------------------------------------- holds */

/** POST /business/appointment-holds — claim slots while a booking is finished. */
export const createHold = async (branchId, services, clientId = null) => {
  const payload = {
    branch_id: branchId,
    services: services.map((line) => ({
      service_id: line.serviceId,
      staff_id: line.staffId || null,
      starts_at: line.startsAt,
    })),
  };

  if (clientId) payload.client_id = clientId;

  const { data } = await api.post("/business/appointment-holds", payload);
  return data.data.hold;
};

/** GET /business/appointment-holds/{token} */
export const fetchHold = async (token) => {
  const { data } = await api.get(`/business/appointment-holds/${token}`);
  return data.data.hold;
};

/** PUT /business/appointment-holds/{token} — bounded; refuses past the ceiling. */
export const extendHold = async (token) => {
  const { data } = await api.put(`/business/appointment-holds/${token}`);
  return data.data.hold;
};

/** DELETE /business/appointment-holds/{token} */
export const releaseHold = (token) => api.delete(`/business/appointment-holds/${token}`);

/* ------------------------------------------------------------- blocked time */

/** GET /business/branches/{branch}/blocked-times */
export const fetchBlockedTimes = async (branchId, from = null, to = null) => {
  const params = {};
  if (from) params.from = from;
  if (to) params.to = to;

  const { data } = await api.get(`/business/branches/${branchId}/blocked-times`, { params });
  return data.data.blocked_times;
};

/**
 * POST /business/branches/{branch}/blocked-times
 *
 * Returns `{ block, displaces }`. A block over existing bookings is allowed on
 * purpose — the business already knows it is doing it — and `displaces` is the
 * list of people to ring.
 */
export const createBlockedTime = async (branchId, values) => {
  const { data } = await api.post(`/business/branches/${branchId}/blocked-times`, {
    branch_id: branchId,
    staff_id: values.staffId || null,
    starts_at: values.startsAt,
    ends_at: values.endsAt,
    reason: values.reason || null,
  });

  return { block: data.data.blocked_time, displaces: data.data.displaces ?? [] };
};

/** DELETE /business/branches/{branch}/blocked-times/{id} */
export const deleteBlockedTime = (branchId, id) =>
  api.delete(`/business/branches/${branchId}/blocked-times/${id}`);
